import { enableProdMode, LOCALE_ID, ErrorHandler, importProvidersFrom, provideZonelessChangeDetection, provideAppInitializer, inject } from '@angular/core';
import { UtilService } from './app/services/util/util.service';
import { MenuService } from './app/services/menu/menu.service';
import { getDatepickerConfig, getDateRangepickerConfig } from './app/app.module';
import { environment } from './environments/environment';
import { BsDatepickerConfig, BsDaterangepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { PERFECT_SCROLLBAR_CONFIG, PerfectScrollbarConfigInterface, PerfectScrollbarModule } from 'ngx-perfect-scrollbar-portable';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { RECAPTCHA_V3 } from './app/config';
import { GlobalErrorHandler } from './app/validators/global-error-handler';
import { provideNgxMask, NgxMaskOptions } from 'ngx-mask';
import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withJsonpSupport } from '@angular/common/http';
import { tokenInterceptor } from './app/services/token/token-interceptor.service';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslateService } from '@ngx-translate/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { routes } from './app/app-routing.module';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { SidebarModule } from 'ngx-angular-sidebar';
import { ModalModule } from 'ngx-bootstrap/modal';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AppComponent } from './app/app.component';

// Register Froala mention plugin before any editor instance is created
import './app/plugins/froala-mention.plugin';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  wheelPropagation: true,
  suppressScrollX: true
};
const maskConfig: NgxMaskOptions = {
  validation: true,
};

if (environment.production) {
  enableProdMode();
}

// Reversão do PWA: desregistra o Angular Service Worker (ngsw) que tenha sido
// instalado. Ele assumia o escopo "/" e quebrava o push do OneSignal (o push
// chegava no ngsw, que não o exibe). Após o unregister, o OneSignal volta a
// controlar "/" e o push web funciona. Idempotente: sem ngsw, não faz nada.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((reg) => {
      const scriptUrl = (reg.active || reg.waiting || reg.installing)?.scriptURL || '';
      if (scriptUrl.includes('ngsw-worker.js')) {
        reg.unregister();
      }
    });
  }).catch(() => { /* noop */ });
  if ('caches' in window) {
    caches.keys()
      .then((keys) => keys.filter((k) => k.startsWith('ngsw:')).forEach((k) => caches.delete(k)))
      .catch(() => { /* noop */ });
  }
}

// Suppress non-functional Froala/Swiper errors globally.
// Froala's pkgd bundle registers onAny handlers on Swiper instances that
// persist after editor destruction, causing harmless TypeError on resize.
window.addEventListener('error', (event) => {
  const msg = event.message || '';
  const stack = event.error?.stack || '';
  if (
    msg.includes('more_btn') ||
    (msg === 'Uncaught TypeError: Illegal invocation' && (stack.includes('onAny') || stack.includes('resizeHandler')))
  ) {
    event.preventDefault();
  }
});

bootstrapApplication(AppComponent, {
    providers: [
        provideZonelessChangeDetection(),
        provideAppInitializer(() => inject(UtilService).hydrateConfigurationFromStorage()),
        provideAppInitializer(() => inject(MenuService).hydrateLoggedUserFromStorage()),
        provideRouter(routes, withComponentInputBinding()),
        provideAnimations(),
        importProvidersFrom(AngularSvgIconModule.forRoot(), MatProgressBarModule, PerfectScrollbarModule, CollapseModule.forRoot(), BsDropdownModule.forRoot(), SidebarModule.forRoot(), BsDatepickerModule.forRoot(), ModalModule.forRoot(), RecaptchaV3Module),
        { provide: LOCALE_ID, useValue: 'pt-BR' },
        { provide: BsDatepickerConfig, useFactory: getDatepickerConfig },
        { provide: BsDaterangepickerConfig, useFactory: getDateRangepickerConfig },
        { provide: PERFECT_SCROLLBAR_CONFIG, useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG },
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: RECAPTCHA_V3 },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        provideNgxMask(maskConfig),
        DatePipe,
        provideHttpClient(withInterceptors([tokenInterceptor]), withJsonpSupport()),
        ...provideTranslateService(),
        ...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' })
    ]
})
  .catch(err => console.error(err));
